import React from 'react';
import { AudienceLevel, Cohort, LEVEL_LABELS } from '../../types/pathway';
import { moduleById } from '../../data/skillModules';
interface PathwayDocumentProps {
  pathwayName: string;
  company: string;
  cohort: Cohort | null;
  audience: AudienceLevel | 'any';
  /** Module ids in the order the prospect should take them. */
  items: string[];
  /** Optional field labels the rep ticked in the send dialog. */
  fields?: string[];
  /** Render only the cover page — used for the thumbnail in the send dialog. */
  coverOnly?: boolean;
}
const MODULES_PER_PAGE = 3;
export function pageCount(itemCount: number): number {
  return 1 + Math.max(1, Math.ceil(itemCount / MODULES_PER_PAGE));
}
function chunk(items: string[]): string[][] {
  const pages: string[][] = [];
  for (let i = 0; i < items.length; i += MODULES_PER_PAGE) {
    pages.push(items.slice(i, i + MODULES_PER_PAGE));
  }
  return pages;
}
function Page({
  children,
  footerLabel,
  pageNumber,
  totalPages
}: {
  children: React.ReactNode;
  footerLabel: string;
  pageNumber: number;
  totalPages: number;
}) {
  return <div className="pathway-page flex h-[1056px] w-[816px] flex-col bg-white shadow-lg" data-id="element-1595" data-mp="2yg9bdrbsm0hs">
      <div className="h-2 w-full flex-shrink-0 bg-[#DB3725]" data-id="element-1596" data-mp="33fm9x9c10w07" />
      <div className="flex flex-1 flex-col px-16 py-12" data-id="element-1597" data-mp="33g5gjtw87xyx">{children}</div>
      <div className="flex flex-shrink-0 items-center justify-between border-t border-gray-200 px-16 py-5" data-id="element-1598" data-mp="38dnslt5jitb5">
        <span className="text-[10px] text-gray-500" data-id="element-1599" data-mp="2as9myy1yem6d">{footerLabel}</span>
        <span className="text-[10px] text-gray-500" data-id="element-1600" data-mp="2fpi5x0uxeu8o">
          Page {pageNumber} of {totalPages}
        </span>
      </div>
    </div>;
}
function Wordmark() {
  return <div className="flex items-baseline gap-2" data-id="element-1601" data-mp="2knbaxqkhti7d">
      <span className="text-lg font-bold text-[#DB3725]" data-id="element-1602" data-mp="2pgwavo5ncm97">
        University of Phoenix
      </span>
      <span className="text-sm font-semibold text-gray-700" data-id="element-1603" data-mp="1rzfemmyu5sb4">
        Professional Development
      </span>
    </div>;
}
export function PathwayDocument({
  pathwayName,
  company,
  cohort,
  audience,
  items,
  fields = [],
  coverOnly = false
}: PathwayDocumentProps) {
  const showCategory = fields.includes('Category');
  const showTopics = fields.includes('Topics');
  const showModuleId = fields.includes('Skill Module ID');
  const contentPages = chunk(items);
  const totalPages = contentPages.length + 1;
  const recipient = company.trim() || 'your team';
  const today = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  const footerLabel = `${pathwayName || 'Recommended pathway'} · Prepared for ${recipient}`;
  return <div className="flex flex-col items-center gap-8" data-id="element-1604" data-mp="1fz7bvoa651fk">
      <Page footerLabel="University of Phoenix · Professional Development" pageNumber={1} totalPages={totalPages} data-id="element-1605" data-mp="1az2xq4wqgisi">
        <Wordmark data-id="element-1606" data-mp="2rc1gqurvsrq4" />

        <div className="mt-20" data-id="element-1607" data-mp="2rbhzzhdyw9do">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#30718D]" data-id="element-1608" data-mp="2me89rzir7tos">
            Recommended learning pathway
          </div>
          <h1 className="mt-3 text-[40px] font-bold leading-tight text-gray-900" data-id="element-1609" data-mp="85p8jxx0sf83">
            {pathwayName || 'Recommended Learning Pathway'}
          </h1>
          <p className="mt-4 text-lg text-gray-700" data-id="element-1610" data-mp="3vbgaikxwc2an">
            Prepared for {recipient}
            {cohort ? ` · ${cohort.name}` : ''}
          </p>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1611" data-mp="45f3cnh1xsua">{today}</p>
        </div>

        <div className="mt-12 grid grid-cols-2 gap-6 border-y border-gray-200 py-6" data-id="element-1612" data-mp="rviys7k36ma7">
          <div data-id="element-1613" data-mp="vwb9744p2x82">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1614" data-mp="vurrd93e4e46">
              Built for
            </div>
            <div className="mt-1 text-2xl font-bold text-gray-900" data-id="element-1615" data-mp="i2u0kedjr4wt">
              {audience === 'any' ? 'Mixed' : LEVEL_LABELS[audience]}
            </div>
          </div>
          <div data-id="element-1616" data-mp="1arr75k8ebd9q">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1617" data-mp="1ar915vvxlkom">
              Modules
            </div>
            <div className="mt-1 text-2xl font-bold text-gray-900" data-id="element-1618" data-mp="3n3dl0hm2kgka">
              {items.length}
            </div>
          </div>
        </div>

        <p className="mt-8 max-w-[34rem] text-sm leading-relaxed text-gray-700" data-id="element-1619" data-mp="3i2z92ohckmgz">
          This pathway was built from our conversation about{' '}
          {recipient === 'your team' ? 'your team' : recipient} and is listed in
          the order we recommend taking it. It is designed for{' '}
          <span className="font-semibold" data-id="element-1620" data-mp="9nfmu9f3g4bf">
            {audience === 'any' ? 'a mixed-experience group' : LEVEL_LABELS[audience].toLowerCase()}
          </span>
          .
        </p>

        <div className="mt-10" data-id="element-1621" data-mp="ukolzo1n8v5">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1622" data-mp="3s2q3yr9tl3hl">
            At a glance
          </div>
          <ol className="mt-3 space-y-2" data-id="element-1623" data-mp="tcyad0w3v4dv">
            {items.map((moduleId, index) => {
            const module = moduleById(moduleId);
            if (!module) return null;
            return <li key={moduleId} className="flex items-baseline gap-3" data-id="element-1624" data-mp="vy64yle7xpx9" data-mpi={index}>
                  <span className="w-5 flex-shrink-0 text-xs font-bold text-[#30718D]" data-id="element-1625" data-mp="10vzbxgncinyl">
                    {index + 1}
                  </span>
                  <span className="flex-1 text-sm font-semibold text-gray-900" data-id="element-1626" data-mp="15tr142wlvvz3">
                    {module.name}
                  </span>
                </li>;
          })}
          </ol>
        </div>

        <div className="mt-auto pt-10" data-id="element-1627" data-mp="1etw7qatz6qc7">
          <p className="text-[10px] leading-relaxed text-gray-500" data-id="element-1628" data-mp="1jujv8mizwp1o">
            Prepared by University of Phoenix Professional Development. Module
            content and availability are subject to change. Contact your account
            team for enrollment and pricing.
          </p>
        </div>
      </Page>

      {!coverOnly && contentPages.map((page, pageIndex) => <Page key={`modules-${pageIndex}`} footerLabel={footerLabel} pageNumber={pageIndex + 2} totalPages={totalPages} data-id="element-1629" data-mp="22lbdyeovhrsq" data-mpi={pageIndex}>
            <div className="flex items-baseline justify-between border-b border-gray-200 pb-4" data-id="element-1630" data-mp="2gz1h7jlm3o6">
              <div data-id="element-1631" data-mp="3ulb1pog620cj">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#30718D]" data-id="element-1632" data-mp="c8v3gtot2dxo">
                  The pathway
                  {pageIndex > 0 ? ' (continued)' : ''}
                </div>
                <h2 className="mt-1 text-2xl font-bold text-gray-900" data-id="element-1633" data-mp="m72hp4rpmhvz">
                  Modules {pageIndex * MODULES_PER_PAGE + 1}–
                  {pageIndex * MODULES_PER_PAGE + page.length} of {items.length}
                </h2>
              </div>
              <span className="text-xs text-gray-500" data-id="element-1634" data-mp="v77nxbl63a8k">
                Take these in the order shown.
              </span>
            </div>

            <div className="mt-2" data-id="element-1635" data-mp="2wz3x4ejv4806">
              {page.map((moduleId, indexOnPage) => {
          const module = moduleById(moduleId);
          if (!module) return null;
          return <article key={moduleId} className="border-b border-gray-100 py-6 last:border-b-0" data-id="element-1636" data-mp="17hqc1adooruq" data-mpi={indexOnPage}>
                    <div className="flex items-baseline gap-3" data-id="element-1637" data-mp="1cjfq6yw7dln1">
                      <span className="text-lg font-bold text-[#DB3725]" data-id="element-1638" data-mp="yp56wx2z6jod">
                        {pageIndex * MODULES_PER_PAGE + indexOnPage + 1}
                      </span>
                      <div data-id="element-1639" data-mp="orh9fzdziy2i">
                        <h3 className="text-[17px] font-bold leading-snug text-gray-900" data-id="element-1640" data-mp="oq5ud3f3a3w3">
                          {module.name}
                        </h3>
                        {(showCategory || showModuleId) && <p className="mt-0.5 text-xs text-gray-500" data-id="element-1641" data-mp="ft5btdepb00k">
                            {[showCategory ? module.category : null, showModuleId ? module.courseId : null].filter(Boolean).join(' · ')}
                          </p>}
                      </div>
                    </div>

                    <dl className="mt-4 space-y-3 pl-7" data-id="element-1642" data-mp="wzjpo477jr9v">
                      <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1643" data-mp="s78rb5s48djf">
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1644" data-mp="s5fm7ux5i1dj">
                          Description
                        </dt>
                        <dd className="text-[13px] leading-relaxed text-gray-800" data-id="element-1645" data-mp="j8f05uxk0fd9">
                          {module.description}
                        </dd>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1646" data-mp="99pia25nqux0">
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1647" data-mp="4a30rgisqsie">
                          Relevance
                        </dt>
                        <dd className="text-[13px] leading-relaxed text-gray-800" data-id="element-1648" data-mp="23kb7hzqaz132">
                          {module.relevance}
                        </dd>
                      </div>
                      <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1649" data-mp="272igv9x2ijp3">
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1650" data-mp="2b4vj3oclvzh4">
                          What you’ll learn
                        </dt>
                        <dd data-id="element-1651" data-mp="2kw9fvod6al6k">
                          <ul className="space-y-1" data-id="element-1652" data-mp="2kvhwrd0rifx6">
                            {module.outcomes.map(outcome => <li key={outcome} className="flex gap-2 text-[13px] leading-relaxed text-gray-800" data-id="element-1653" data-mp="1o7tlnez4bnn4">
                                <span aria-hidden="true" className="mt-[7px] h-1 w-1 flex-shrink-0 rounded-full bg-[#30718D]" data-id="element-1654" data-mp="1x8rrm2t4a58c" />
                                {outcome}
                              </li>)}
                          </ul>
                        </dd>
                      </div>
                      {showTopics && <div className="grid grid-cols-[120px_1fr] gap-4" data-id="element-1655" data-mp="2d6qr09nfehg1">
                          <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-1656" data-mp="2hyrzdkhh3o0s">
                            Topics
                          </dt>
                          <dd className="text-[13px] leading-relaxed text-gray-800" data-id="element-1657" data-mp="1pamcos6uzoyb">
                            {module.topics.join(' · ')}
                          </dd>
                        </div>}
                    </dl>
                  </article>;
        })}
            </div>
          </Page>)}
    </div>;
}